/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Voice Catalog & Multi-Provider Audition Studio View (Task 10.5.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Mic,
  Volume2,
  Search,
  Filter,
  RotateCcw,
  Star,
  Layers,
  Radio,
  CheckCircle2,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import {
  useVoiceCatalogStore,
  VoiceProvider,
  VoiceToneStyle,
  VoiceItem,
} from '../stores/useVoiceCatalogStore';
import { VoiceCard } from '../components/voice/VoiceCard';
import { VoiceAuditionPlayer } from '../components/voice/VoiceAuditionPlayer';
import { NewVoiceCloneModal } from '../components/voice/NewVoiceCloneModal';

interface VoiceCatalogViewProps {
  onSelectVoice?: (voice: VoiceItem) => void;
  selectedVoiceId?: string;
}

export const VoiceCatalogView: React.FC<VoiceCatalogViewProps> = ({
  onSelectVoice,
  selectedVoiceId,
}) => {
  const { t } = useTranslation();
  const {
    activeTab,
    searchQuery,
    providerFilter,
    languageFilter,
    genderFilter,
    styleFilter,
    setActiveTab,
    setSearchQuery,
    setProviderFilter,
    setLanguageFilter,
    setGenderFilter,
    setStyleFilter,
    resetFilters,
    getFilteredVoices,
    getVoiceStats,
  } = useVoiceCatalogStore();

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [successNotification, setSuccessNotification] = useState<string | null>(null);

  const filteredVoices = getFilteredVoices();
  const stats = getVoiceStats();

  const isFiltered =
    searchQuery.trim() !== '' ||
    providerFilter !== 'all' ||
    languageFilter !== 'all' ||
    genderFilter !== 'all' ||
    styleFilter !== 'all';

  const handleVoiceUsed = (voiceId: string) => {
    const voice = filteredVoices.find((v) => v.id === voiceId);
    if (voice && onSelectVoice) {
      onSelectVoice(voice);
    }
    setSuccessNotification(t('voice.voiceSelectedSuccess'));
    setTimeout(() => {
      setSuccessNotification(null);
    }, 3000);
  };

  const handleVoiceClonedSuccess = () => {
    setActiveTab('cloned');
    setSuccessNotification(t('voice.cloneCreatedSuccess'));
    setTimeout(() => {
      setSuccessNotification(null);
    }, 3000);
  };

  return (
    <div className="space-y-6 pb-28">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="size-8 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <Volume2 className="size-4.5" />
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground">
              {t('voice.title')}
            </h1>
          </div>
          <p className="text-xs text-foreground-muted max-w-2xl">
            {t('voice.subtitle')}
          </p>
        </div>

        <button
          type="button"
          onClick={() => setIsCloneModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-sm transition-colors shrink-0"
        >
          <Mic className="size-3.5" />
          <span>{t('voice.createClone')}</span>
        </button>
      </div>

      {/* Success Notification Alert */}
      {successNotification && (
        <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-medium animate-slide-up">
          <CheckCircle2 className="size-4 shrink-0" />
          <span>{successNotification}</span>
        </div>
      )}

      {/* Metrics Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Radio className="size-4.5" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {stats.totalVoices}
            </div>
            <div className="text-[11px] text-foreground-muted">{t('voice.metricTotalVoices')}</div>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-emerald-500/10 text-emerald-500 flex items-center justify-center shrink-0">
            <Layers className="size-4.5" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {stats.providerCount}
            </div>
            <div className="text-[11px] text-foreground-muted">{t('voice.metricProviders')}</div>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
            <Mic className="size-4.5" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {stats.clonedCount}
            </div>
            <div className="text-[11px] text-foreground-muted">{t('voice.metricCloned')}</div>
          </div>
        </div>

        <div className="p-3.5 rounded-xl bg-surface border border-surface-border flex items-center gap-3">
          <div className="size-9 rounded-lg bg-amber-500/10 text-amber-500 flex items-center justify-center shrink-0">
            <Star className="size-4.5" />
          </div>
          <div>
            <div className="text-base font-bold font-mono text-foreground">
              {stats.favoritesCount}
            </div>
            <div className="text-[11px] text-foreground-muted">{t('voice.metricFavorites')}</div>
          </div>
        </div>
      </div>

      {/* Main Filter & Navigation Panel */}
      <div className="p-4 rounded-2xl bg-surface border border-surface-border space-y-4 shadow-xs">
        {/* Top Controls: Tabs & Reset */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-surface-border pb-3">
          {/* Tabs */}
          <div className="flex items-center gap-1.5 p-1 bg-surface-elevated rounded-xl border border-surface-border">
            <button
              type="button"
              onClick={() => setActiveTab('all')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'all'
                  ? 'bg-primary text-primary-fg shadow-xs'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <span>{t('voice.tabAllVoices')}</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-surface/30">
                {stats.totalVoices}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('cloned')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'cloned'
                  ? 'bg-primary text-primary-fg shadow-xs'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <span>{t('voice.tabClonedVoices')}</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-surface/30">
                {stats.clonedCount}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('favorites')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors flex items-center gap-2 ${
                activeTab === 'favorites'
                  ? 'bg-primary text-primary-fg shadow-xs'
                  : 'text-foreground-muted hover:text-foreground'
              }`}
            >
              <span>{t('voice.tabFavorites')}</span>
              <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-surface/30">
                {stats.favoritesCount}
              </span>
            </button>
          </div>

          {/* Reset Filters Action */}
          {isFiltered && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium self-end md:self-auto transition-colors"
            >
              <RotateCcw className="size-3" />
              <span>{t('voice.resetFilters')}</span>
            </button>
          )}
        </div>

        {/* Filter Toolbar: Search, Provider, Language */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="size-3.5 text-foreground-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('voice.searchPlaceholder')}
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:border-primary transition-colors"
            />
          </div>

          {/* Provider Filter */}
          <div className="relative">
            <select
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value as 'all' | VoiceProvider)}
              className="w-full px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground focus:outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">{t('voice.filterProviderAll')}</option>
              <option value="openai">OpenAI (TTS-1 / HD)</option>
              <option value="elevenlabs">ElevenLabs Multilingual</option>
              <option value="google">Google Cloud Neural2</option>
              <option value="azure">Microsoft Azure Speech</option>
              <option value="custom_clone">{t('voice.filterProviderCloned')}</option>
            </select>
          </div>

          {/* Language Filter */}
          <div className="relative">
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
              className="w-full px-3 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground focus:outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">{t('voice.filterLangAll')}</option>
              <option value="vi-VN">{t('voice.langVi')}</option>
              <option value="en-US">{t('voice.langEnUs')}</option>
              <option value="en-GB">{t('voice.langEnGb')}</option>
              <option value="ja-JP">{t('voice.langJa')}</option>
              <option value="zh-CN">{t('voice.langZh')}</option>
              <option value="fr-FR">{t('voice.langFr')}</option>
              <option value="de-DE">{t('voice.langDe')}</option>
              <option value="es-ES">{t('voice.langEs')}</option>
            </select>
          </div>
        </div>

        {/* Tag Pills: Gender & Style */}
        <div className="flex flex-wrap items-center gap-3 pt-1">
          {/* Gender Filter Pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-semibold text-foreground-muted mr-1">
              {t('voice.labelGender')}:
            </span>
            {(['all', 'female', 'male', 'neutral'] as const).map((gender) => (
              <button
                key={gender}
                type="button"
                onClick={() => setGenderFilter(gender)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                  genderFilter === gender
                    ? 'bg-primary text-primary-fg'
                    : 'bg-surface-elevated text-foreground-muted hover:text-foreground'
                }`}
              >
                {t(`voice.gender_${gender}`)}
              </button>
            ))}
          </div>

          <div className="h-4 w-px bg-surface-border hidden sm:block" />

          {/* Style / Tone Filter Pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] font-semibold text-foreground-muted mr-1">
              {t('voice.labelToneStyle')}:
            </span>
            {(
              [
                'all',
                'warm',
                'energetic',
                'authoritative',
                'conversational',
                'news',
                'storytelling',
                'empathetic',
              ] as const
            ).map((style) => (
              <button
                key={style}
                type="button"
                onClick={() => setStyleFilter(style as 'all' | VoiceToneStyle)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors capitalize ${
                  styleFilter === style
                    ? 'bg-primary text-primary-fg'
                    : 'bg-surface-elevated text-foreground-muted hover:text-foreground'
                }`}
              >
                {t(`voice.style_${style}`)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Voice Grid Section */}
      <div>
        <div className="flex items-center justify-between mb-3 px-1">
          <div className="text-xs font-semibold text-foreground-muted">
            {t('voice.showingVoicesCount', { count: filteredVoices.length })}
          </div>
        </div>

        {filteredVoices.length === 0 ? (
          <div className="p-12 text-center rounded-2xl bg-surface border border-surface-border space-y-3">
            <div className="size-12 rounded-full bg-surface-elevated text-foreground-muted flex items-center justify-center mx-auto">
              <Filter className="size-6" />
            </div>
            <h3 className="text-sm font-bold text-foreground">
              {t('voice.noVoicesFoundTitle')}
            </h3>
            <p className="text-xs text-foreground-muted max-w-sm mx-auto">
              {t('voice.noVoicesFoundDesc')}
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-elevated text-foreground text-xs font-medium hover:bg-surface-border transition-colors mt-2"
            >
              <RotateCcw className="size-3" />
              <span>{t('voice.resetFilters')}</span>
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredVoices.map((voice) => (
              <VoiceCard
                key={voice.id}
                voice={voice}
                isSelected={selectedVoiceId === voice.id}
                onSelectVoice={onSelectVoice}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sticky Bottom Audition Player Bar */}
      <VoiceAuditionPlayer onUseVoice={handleVoiceUsed} />

      {/* Voice Clone Modal */}
      <NewVoiceCloneModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onVoiceCloned={handleVoiceClonedSuccess}
      />
    </div>
  );
};
